import React, { useContext, useEffect, useState } from 'react';
import { Button } from '../../../components/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import { useNavigate } from 'react-router-dom';
import { useAppPreCallPlans } from '../../../hooks/useAppPreCallPlan';
import { CalendarDaysIcon, EllipsisHorizontalIcon, EyeIcon, PrinterIcon, XMarkIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownDivider, DropdownItem, DropdownLabel, DropdownMenu } from '../../../components/Dropdown';
import dateFormat from 'dateformat';
import { Heading } from '../../../components/Heading';
import Pagination from '../../../components/Pagination';
import Loading from '../../../components/Loading';
import { generateMultiplePreCallPlansPDF, printPDF } from '../../../utils/pdfUtils';
import showSuccessToast from '../../../components/Toasts/ShowSuccessToast';
import showErrorToast from '../../../components/Toasts/ShowSuccessToast';
import PrintSummaryDialog from '../../../components/PrintSummaryDialog';
import { PreCallPlan } from '../../../models/AppPreCallPlan';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SelectToggleButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { Select } from '../../../components/Select';
import { Text } from '../../../components/Text';
import { useAppUser } from '../../../hooks/useAppUser';
import { useWarehouses } from '../../../hooks/useWarehouse';
import { UserContext } from '../../../context/userContext';

const COLUMNS = [
    { key: 'salesman', label: 'Salesman' },
    { key: 'customer', label: 'Customer' },
    { key: 'date', label: 'Planned Date' },
];
const SORT_OPTIONS = [
    { label: 'Newest first', value: 'newest' },
    { label: 'Oldest first', value: 'oldest' },
];

const AppPreCallPlanPage: React.FC = () => {
    const { currentUser } = useContext(UserContext);
    const { loading, error, fetchPreCallPlansPaginated, deletePreCallPlan, preCallPlans, total, fetchPreCallPlansForToday, fetchPreCallPlansForThisWeek } = useAppPreCallPlans();
    const { appUsers } = useAppUser();
    const { warehouses } = useWarehouses();
    const [searchQuery, setSearchQuery] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterSalesman, setFilterSalesman] = useState('');
    const [filterWarehouseId, setFilterWarehouseId] = useState('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const perPage = 10;
    const [isDeleting, setIsDeleting] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const [showPrintDialog, setShowPrintDialog] = useState(false);
    const [printDialogData, setPrintDialogData] = useState<{
        plans: PreCallPlan[];
        title: string;
        dateRange: string;
    } | null>(null);

    // Selection state
    const [selectedPlans, setSelectedPlans] = useState<Set<number>>(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [selectMode, setSelectMode] = useState(false);

    const navigate = useNavigate();
    const [sortOrder, setSortOrder] = useState('newest');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));

    useEffect(() => {
        document.title = "Pre Call Plans";
        fetchPreCallPlansPaginated(currentPage, perPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchPreCallPlansPaginated(currentPage, perPage, searchQuery || undefined, filterWarehouseId || undefined, filterSalesman || undefined);
    }, [currentPage, searchQuery, filterWarehouseId, filterSalesman, fetchPreCallPlansPaginated]);

    const resetFilters = () => {
        setFilterSalesman('');
        setFilterWarehouseId('');
    };

    const activeFilterCount = [filterSalesman, filterWarehouseId].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterSalesman ? [{
            key: 'salesman',
            label: `Salesman: ${appUsers?.find((u) => u.id?.toString() === filterSalesman)?.username || filterSalesman}`,
            onClear: () => setFilterSalesman(''),
        }] : []),
        ...(filterWarehouseId ? [{
            key: 'warehouse',
            label: `Warehouse: ${warehouses?.find((w) => w.id?.toString() === filterWarehouseId)?.warehouse_name || filterWarehouseId}`,
            onClear: () => setFilterWarehouseId(''),
        }] : []),
    ];

    // Reset selection when page or search changes
    useEffect(() => {
        clearSelection();
    }, [currentPage, searchQuery]);

    const handleDelete = async (id: number) => {
        setIsDeleting(true);
        try {
            await deletePreCallPlan(id);
            fetchPreCallPlansPaginated(currentPage, perPage, searchQuery || undefined);
        } catch (error) {
            console.error('Failed to delete pre call plan:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const handlePrintToday = async () => {
        try {
            const todayPlans = await fetchPreCallPlansForToday();
            const today = new Date();
            const dateRange = `Today (${today.toLocaleDateString()})`;

            setPrintDialogData({
                plans: todayPlans,
                title: 'Print Today\'s Pre-Call Plans',
                dateRange
            });
            setShowPrintDialog(true);
        } catch (error) {
            console.error('Error fetching today\'s plans:', error);
            showErrorToast('Failed to fetch today\'s plans');
        }
    };

    const handlePrintThisWeek = async () => {
        try {
            const weekPlans = await fetchPreCallPlansForThisWeek();
            const today = new Date();
            const startOfWeek = new Date(today);
            startOfWeek.setDate(today.getDate() - today.getDay());
            const endOfWeek = new Date(today);
            endOfWeek.setDate(today.getDate() + (6 - today.getDay()));

            const dateRange = `${startOfWeek.toLocaleDateString()} - ${endOfWeek.toLocaleDateString()}`;

            setPrintDialogData({
                plans: weekPlans,
                title: 'Print This Week\'s Pre-Call Plans',
                dateRange
            });
            setShowPrintDialog(true);
        } catch (error) {
            console.error('Error fetching this week\'s plans:', error);
            showErrorToast('Failed to fetch this week\'s plans');
        }
    };

    const handleConfirmPrint = async () => {
        if (!printDialogData) return;

        setIsPrinting(true);
        try {
            const { plans, title } = printDialogData;
            const doc = generateMultiplePreCallPlansPDF(plans, title);
            const filename = `PreCallPlans_${new Date().toISOString().split('T')[0]}.pdf`;
            printPDF(doc, filename);
            showSuccessToast(`Generated PDF report for ${plans.length} plans`);
            setShowPrintDialog(false);
            setPrintDialogData(null);
        } catch (error) {
            console.error('Error generating PDF:', error);
            showErrorToast('Failed to generate PDF report');
        } finally {
            setIsPrinting(false);
        }
    };

    const handleClosePrintDialog = () => {
        setShowPrintDialog(false);
        setPrintDialogData(null);
    };

    // Selection handlers
    const handlePlanSelection = (planId: number, checked: boolean) => {
        const newSelectedPlans = new Set(selectedPlans);
        if (checked) {
            newSelectedPlans.add(planId);
        } else {
            newSelectedPlans.delete(planId);
        }
        setSelectedPlans(newSelectedPlans);

        // Update select all state
        if (checked && newSelectedPlans.size === preCallPlans.length) {
            setSelectAll(true);
        } else if (!checked) {
            setSelectAll(false);
        }
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            const allPlanIds = preCallPlans.map(plan => plan.id);
            setSelectedPlans(new Set(allPlanIds));
            setSelectAll(true);
        } else {
            setSelectedPlans(new Set());
            setSelectAll(false);
        }
    };

    const handlePrintSelected = async () => {
        if (selectedPlans.size === 0) {
            showErrorToast('Please select at least one plan to print');
            return;
        }

        const selectedPlansData = preCallPlans.filter(plan => selectedPlans.has(plan.id));

        setPrintDialogData({
            plans: selectedPlansData,
            title: `Print Selected Pre-Call Plans (${selectedPlans.size})`,
            dateRange: `Selected Plans`
        });
        setShowPrintDialog(true);
    };

    const clearSelection = () => {
        setSelectedPlans(new Set());
        setSelectAll(false);
    };

    return (
        <div>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Pre Call Plans</Heading>
                </div>
                <div className="flex items-center space-x-3">
                    {selectedPlans.size > 0 && (
                        <span className="text-sm text-gray-600">
                            {selectedPlans.size} plan{selectedPlans.size !== 1 ? 's' : ''} selected
                        </span>
                    )}
                    <Dropdown>
                        <DropdownButton outline>
                            <EllipsisHorizontalIcon />
                            More
                        </DropdownButton>
                        <DropdownMenu anchor="bottom end">
                            {selectedPlans.size > 0 && (
                                <>
                                    <DropdownItem onClick={handlePrintSelected} disabled={isPrinting}>
                                        <PrinterIcon />
                                        <DropdownLabel>
                                            {isPrinting ? 'Generating...' : `Print Selected (${selectedPlans.size})`}
                                        </DropdownLabel>
                                    </DropdownItem>
                                    <DropdownItem onClick={clearSelection}>
                                        <XMarkIcon />
                                        <DropdownLabel>Clear Selection</DropdownLabel>
                                    </DropdownItem>
                                    <DropdownDivider />
                                </>
                            )}
                            <DropdownItem onClick={handlePrintToday} disabled={isPrinting}>
                                <PrinterIcon />
                                <DropdownLabel>{isPrinting ? 'Generating...' : 'Print Today'}</DropdownLabel>
                            </DropdownItem>
                            <DropdownItem onClick={handlePrintThisWeek} disabled={isPrinting}>
                                <PrinterIcon />
                                <DropdownLabel>{isPrinting ? 'Generating...' : 'Print This Week'}</DropdownLabel>
                            </DropdownItem>
                            <DropdownItem onClick={() => navigate('/gp/app/precallplans/calendar')}>
                                <CalendarDaysIcon />
                                <DropdownLabel>Calendar View</DropdownLabel>
                            </DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </div>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search pre call plans…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SelectToggleButton active={selectMode} onClick={() => { setSelectMode((v) => !v); clearSelection(); }} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />

            <ul className="mt-6">
                {loading && <div className="w-full text-center h-full py-36 justify-center"><Loading /></div>}
                {error && <p className="py-16 text-center text-red-700">{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {selectMode && (
                                        <TableHeader>
                                            <input
                                                type="checkbox"
                                                checked={selectAll}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                title="Select all plans on this page"
                                                aria-label="Select all plans on this page"
                                            />
                                        </TableHeader>
                                    )}
                                    <TableHeader>S/N</TableHeader>
                                    {isColumnVisible(columnVisibility, 'salesman') && <TableHeader>Salesman</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'customer') && <TableHeader>Customer</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'date') && <TableHeader>Planned Date</TableHeader>}
                                    <TableHeader className="text-center">Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {preCallPlans.length > 0 ? [...preCallPlans].sort((a, b) => {
                                    const diff = new Date(a.planned_date || a.created_time).getTime() - new Date(b.planned_date || b.created_time).getTime();
                                    return sortOrder === 'newest' ? -diff : diff;
                                }).map((plan, index) => (
                                    <TableRow
                                        key={plan.id}
                                        className={`${selectedPlans.has(plan.id) ? 'bg-secondary' : ''} hover:bg-secondary`}
                                    >
                                        {selectMode && (
                                            <TableCell>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedPlans.has(plan.id)}
                                                    onChange={(e) => handlePlanSelection(plan.id, e.target.checked)}
                                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                    title={`Select ${plan.customer_fullname} plan`}
                                                    aria-label={`Select ${plan.customer_fullname} plan`}
                                                />
                                            </TableCell>
                                        )}
                                        <TableCell>{(currentPage - 1) * perPage + index + 1}</TableCell>
                                        {isColumnVisible(columnVisibility, 'salesman') && <TableCell>{plan.salesman_username}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'customer') && <TableCell>{plan.customer_fullname}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'date') && <TableCell>{dateFormat(plan.planned_date || plan.created_time, "mmm dd, yyyy")}</TableCell>}
                                        <TableCell className="py-5 flex gap-4 text-zinc-500 justify-center">
                                            <a onClick={() => navigate(`/gp/app/precallplans/view/${plan.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <EyeIcon className='h-5 w-5' />
                                            </a>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8">No pre call plan found.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>

                        {/* Selection Summary */}
                        {selectedPlans.size > 0 && (
                            <div className="mt-4 p-4 bg-secondary rounded-xl">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                        <span className="text-sm font-medium text-blue-900">
                                            {selectedPlans.size} plan{selectedPlans.size !== 1 ? 's' : ''} selected
                                        </span>
                                        <span className="text-sm text-blue-700">
                                            {preCallPlans.filter(plan => selectedPlans.has(plan.id)).map(plan => plan.customer_fullname).join(', ')}
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Button
                                            onClick={handlePrintSelected}
                                            disabled={isPrinting}
                                            className="flex items-center gap-2"
                                        >
                                            <PrinterIcon className="h-4 w-4" />
                                            {isPrinting ? 'Generating...' : `Print Selected Plans`}
                                        </Button>
                                        <Button
                                            onClick={clearSelection}
                                            outline
                                            className="text-sm"
                                        >
                                            Clear Selection
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <Pagination
                            currentPage={currentPage}
                            totalPages={Math.ceil(total / perPage)}
                            onPageChange={setCurrentPage}
                            totalResult={total}
                            dataName={'Pre Call Plans'}
                        />
                    </div>
                )}
            </ul>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div>
                    <Text>By Salesman</Text>
                    <Select value={filterSalesman} onChange={(e) => setFilterSalesman(e.target.value)}>
                        <option value="">All Salesman</option>
                        {appUsers?.map((user) => (
                            <option key={user.id} value={user.id?.toString()}>
                                {user.username}
                            </option>
                        ))}
                    </Select>
                </div>
                {(currentUser?.role?.toString() === "1") && (
                    <div>
                        <Text>By Warehouse</Text>
                        <Select value={filterWarehouseId} onChange={(e) => setFilterWarehouseId(e.target.value)}>
                            <option value="">All Warehouses</option>
                            {warehouses?.map((warehouse) => (
                                <option key={warehouse.id} value={warehouse.id?.toString()}>
                                    {warehouse.warehouse_name}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>

            {/* Print Summary Dialog */}
            {printDialogData && (
                <PrintSummaryDialog
                    isOpen={showPrintDialog}
                    onClose={handleClosePrintDialog}
                    onConfirm={handleConfirmPrint}
                    plans={printDialogData.plans}
                    title={printDialogData.title}
                    dateRange={printDialogData.dateRange}
                    loading={isPrinting}
                />
            )}
        </div>

    );
};

export default AppPreCallPlanPage;
